/**
 * R19 Payment success — redesign v3 "arch draws" moment. Receives amount
 * (paise) + reference via query params from the Pay tab. No TopBar (matches
 * design + src/app/visitor-approval.tsx precedent), so top inset is applied
 * directly via useSafeAreaInsets.
 *
 * The design animates an SVG arch stroke-drawing in (stroke-dasharray). This
 * app has no react-native-svg (OTA-safe View-only brand asset policy, see
 * src/components/brand/index.tsx GateSplash), so the arch is a locally-built
 * View shape — same open-bottom "gate" band as BeroMark's geometry but in
 * the success tint — revealed top-down through an animated height mask
 * (Animated, no SVG), which is the same "no SVG" approximation GateSplash
 * uses for its own arch-draw intro.
 * Wired: reads amount/reference passed from POST /api/payments/<id>/pay.
 * Port of design/source/screens-pay.jsx PaySuccessScreen.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect } from 'react';
import { Animated, Easing, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { space } from '@bero/tokens';
import { fmtNow, inr } from '@/components/money/bits';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, Screen } from '@/ui';
import type { DictKey } from '@/i18n/en';

/** Open-bottom "gate" band — same topology as BeroMark, success-tinted,
 * revealed top-down via an animated height mask (arch-draw approximation). */
function SuccessArch({ size = 120 }: { size?: number }) {
  const { colors } = useTheme();
  const H = size * (140 / 120);
  const u = H / 58;
  const [reveal] = React.useState(() => new Animated.Value(0));
  const [fig] = React.useState(() => new Animated.Value(0));

  useEffect(() => {
    Animated.timing(reveal, { toValue: 1, duration: 900, easing: Easing.out(Easing.cubic), useNativeDriver: false }).start();
    Animated.timing(fig, { toValue: 1, duration: 450, delay: 750, easing: Easing.out(Easing.ease), useNativeDriver: true }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <View style={{ width: size, height: H }}>
      <Animated.View
        style={{
          position: 'absolute', left: 0, right: 0, top: 0, overflow: 'hidden',
          height: reveal.interpolate({ inputRange: [0, 1], outputRange: [0, H] }),
        }}
      >
        <View
          style={{
            position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u,
            borderWidth: 4 * u, borderBottomWidth: 0, borderColor: colors.success500,
            borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u,
          }}
        />
      </Animated.View>
      <Animated.View style={{ opacity: fig, transform: [{ scale: fig.interpolate({ inputRange: [0, 1], outputRange: [0.7, 1] }) }] }}>
        <View style={{ position: 'absolute', left: 25 * u - 6 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: colors.accent500 }} />
        <View style={{ position: 'absolute', left: 25 * u - 5 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: colors.accent500 }} />
      </Animated.View>
    </View>
  );
}

export default function PaySuccessScreen() {
  const { t } = useT();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ amount?: string; reference?: string }>();

  const amountPaise = Number(params.amount ?? 0);
  const reference = params.reference || '—';
  const paidAt = React.useMemo(() => fmtNow(new Date()), []);

  const rows: [DictKey, string, boolean?][] = [
    ['ps_paid_via', 'UPI', false],
    ['ps_reference', reference, true],
    ['ps_time', paidAt, true],
  ];

  return (
    <Screen padded={false} style={{ paddingTop: insets.top }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, paddingHorizontal: space.s5, paddingVertical: space.s3 }}>
        <BrandLockup markHeight={20} />
      </View>
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: space.s6 }}>
        <SuccessArch size={100} />
        <AppText variant="headingLg" style={{ marginTop: space.s5, textAlign: 'center' }}>{t('ps_paid_thanks')}</AppText>
        <AppText mono weight="500" style={{ fontSize: 32, lineHeight: 38, marginTop: space.s2, color: colors.textPrimary }}>
          {inr(amountPaise)}
        </AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>{t('ps_received')}</AppText>

        <View style={{
          marginTop: space.s6, width: '100%', borderRadius: 16, backgroundColor: colors.canvas1,
          borderWidth: 1, borderColor: colors.borderSoft, padding: space.s4, gap: 10,
        }}>
          {rows.map(([k, v, mono]) => (
            <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <AppText variant="bodySm" tone="tertiary">{t(k)}</AppText>
              <AppText variant="bodySm" weight="600" mono={mono}>{v}</AppText>
            </View>
          ))}
          {/* not wired — no receipt-generation endpoint yet, decorative like the design */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <AppText variant="bodySm" tone="tertiary">{t('ps_receipt')}</AppText>
            <AppText variant="bodySm" weight="600" tone="brand">{t('ps_download_pdf')}</AppText>
          </View>
        </View>

        <Button
          title={t('ps_done')}
          kind="secondary"
          style={{ marginTop: space.s6, minWidth: 160 }}
          onPress={() => router.dismissTo('/(tabs)/pay')}
        />
      </View>
    </Screen>
  );
}
